import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import {
  DEFAULT_SIDEBAR_LAYOUT,
  DRAWER_QUERY,
  isSidebarShortcut,
  isTypingTarget,
  parseSidebarLayout,
  serializeSidebarLayout,
  sidebarWidth,
  type SidebarLayout,
} from '@/lib/sidebar-size'

// Ported from Bilanss (hooks/useSidebarLayout.ts), with x11's own storage key.
const STORAGE_KEY = 'x11-sidebar'

/** Storage can be missing or throw (a private window, blocked site data); the sidebar then opens at its default. */
function readStoredLayout(): SidebarLayout {
  try {
    return parseSidebarLayout(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    return DEFAULT_SIDEBAR_LAYOUT
  }
}

function storeLayout(layout: SidebarLayout) {
  try {
    window.localStorage.setItem(STORAGE_KEY, serializeSidebarLayout(layout))
  } catch {
    // Without storage the layout still holds for this visit.
  }
}

/**
 * The sidebar's width is named on the page root, where the fixed sidebar, its resize edge and the page beside it
 * all read it. With no name the stylesheet's default width for the screen applies (styles/sidebar.css).
 */
export function nameSidebarWidth(width: number | null) {
  const root = document.documentElement.style
  if (width === null) root.removeProperty('--sidebar-width')
  else root.setProperty('--sidebar-width', `${width}px`)
}

export function isMacPlatform(): boolean {
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
}

function useViewportWidth(): number {
  const [width, setWidth] = useState(() => window.innerWidth)

  useEffect(() => {
    const update = () => setWidth(window.innerWidth)
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return width
}

/** Whether the screen is narrow enough for the phone header and drawer, which never fold or resize. */
export function useDrawerMode(): boolean {
  const [drawer, setDrawer] = useState(() => window.matchMedia(DRAWER_QUERY).matches)

  useEffect(() => {
    const media = window.matchMedia(DRAWER_QUERY)
    const update = () => setDrawer(media.matches)
    media.addEventListener('change', update)
    update()
    return () => media.removeEventListener('change', update)
  }, [])

  return drawer
}

export interface SidebarLayoutState {
  layout: SidebarLayout
  /** The sidebar is the icon rail: folded, on a desktop screen. The drawer never folds. */
  rail: boolean
  /** The width the sidebar takes on this screen. */
  width: number
  viewportWidth: number
  setLayout: (layout: SidebarLayout) => void
}

/**
 * The person's sidebar layout, remembered per browser. It is read before the first render and its width is named in
 * a layout effect, before the first paint, so the sidebar never flashes at the wrong width. In drawer mode nothing
 * is named and a stored fold waits for the next desktop-width visit.
 */
export function useSidebarLayout(drawer: boolean): SidebarLayoutState {
  const [layout, setLayoutState] = useState(readStoredLayout)
  const viewportWidth = useViewportWidth()
  const width = sidebarWidth(layout, viewportWidth)
  const chosen = layout.collapsed || layout.width !== null
  const namedWidth = !drawer && chosen ? width : null

  useLayoutEffect(() => {
    nameSidebarWidth(namedWidth)
  }, [namedWidth])
  useLayoutEffect(() => () => nameSidebarWidth(null), [])

  const setLayout = useCallback((next: SidebarLayout) => {
    setLayoutState(next)
    storeLayout(next)
  }, [])

  return { layout, rail: !drawer && layout.collapsed, width, viewportWidth, setLayout }
}

/**
 * What owns the keyboard while it is on screen. A modal dialog: folding the sidebar behind it would change a page the
 * person cannot see or reach until the dialog closes. x11's dialogs are Radix ones (Dialog, AlertDialog, Sheet,
 * Popover), a div with role="dialog" and data-state="open" but no aria-modal, so they are named here beside the
 * native ones. A Radix menu or select list: it is anchored to its trigger, so a fold would move it with the trigger.
 * A menu is matched while it fades out too (Radix keeps data-state on it until it unmounts), so its fade does not
 * show over the nav rows as they fold.
 */
const OWNS_KEYBOARD = [
  'dialog[open]',
  '[aria-modal="true"]',
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state]',
  '[role="listbox"][data-state="open"]',
].join(', ')

function isOverlayOpen(): boolean {
  return document.querySelector(OWNS_KEYBOARD) !== null
}

/**
 * ⌘\ (Ctrl+\ off a Mac) folds and unfolds the sidebar from anywhere on the page, except while focus is in a field,
 * where the keys belong to what is being typed, or while a dialog, a menu or a select list is open.
 */
export function useSidebarShortcut(enabled: boolean, onShortcut: () => void) {
  const latest = useRef(onShortcut)
  latest.current = onShortcut

  useEffect(() => {
    if (!enabled) return
    const mac = isMacPlatform()
    function handle(event: KeyboardEvent) {
      if (event.defaultPrevented || !isSidebarShortcut(event, mac)) return
      if (isTypingTarget(document.activeElement as HTMLElement | null) || isOverlayOpen()) return
      event.preventDefault()
      latest.current()
    }
    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [enabled])
}

/**
 * Folding hides things that may have had focus (a label, the user's name). When focus was in the sidebar and its
 * element is gone or hidden, it moves to the fold key instead of falling back to the top of the page. Call the
 * returned function before a change that folds or unfolds the sidebar.
 */
export function useFocusAfterFold(
  sidebar: RefObject<HTMLElement | null>,
  toggleKey: RefObject<HTMLButtonElement | null>,
  rail: boolean,
): () => void {
  const focusWasInside = useRef(false)

  useEffect(() => {
    if (!focusWasInside.current) return
    focusWasInside.current = false
    const active = document.activeElement
    const stillShown = active instanceof HTMLElement && active !== document.body && active.getClientRects().length > 0
    if (!stillShown) toggleKey.current?.focus()
  }, [rail, toggleKey])

  return useCallback(() => {
    focusWasInside.current = sidebar.current?.contains(document.activeElement) ?? false
  }, [sidebar])
}
